/**
 * src/components/public/blog/ReadingProgress.tsx
 *
 * Makale detay sayfasında sayfa üstüne yapıştırılan ince ilerleme çubuğu.
 * Kullanıcı scroll ederken `<article>` body'nin viewport içinde kalan kısmının
 * yüzdesini gösterir.
 *
 * Client Component — scroll event + RAF throttle.
 */

'use client'

import { useEffect, useRef, useState } from 'react'

interface ReadingProgressProps {
  /** Hedef elementin CSS selector'ı (gövdeyi içeren container). */
  targetSelector?: string
}

export function ReadingProgress({
  targetSelector = '#makale-body',
}: ReadingProgressProps) {
  const [progress, setProgress] = useState(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const target = document.querySelector<HTMLElement>(targetSelector)
    if (!target) return

    function compute() {
      if (!target) return
      const rect = target.getBoundingClientRect()
      const totalHeight = rect.height + window.innerHeight
      const scrolled = -rect.top + window.innerHeight
      const ratio = Math.max(0, Math.min(1, scrolled / totalHeight))
      setProgress(ratio * 100)
    }

    function onScroll() {
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(() => {
        compute()
        rafRef.current = null
      })
    }

    compute()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [targetSelector])

  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 h-1 z-50 pointer-events-none"
    >
      <div
        className="h-full bg-gradient-to-r from-brand-500 via-brand-600 to-brand-800 transition-[width] duration-100"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}
